// 🔧 디바이스 데이터 생성 헬퍼 함수들
// [배경] 반복되는 데이터 생성 패턴을 함수로 추출하여 코드 중복 제거
// [SSOT] generateModelId()와 동일한 ID 규칙을 사용하여 일관성 보장

import type { UnifiedDevice, Brand, DeviceCategory } from '../unifiedDeviceData';
import { generateModelId } from '../../types/deviceCatalog';

// 🔴 DEPRECATED: 이 함수는 더 이상 사용하지 않습니다.
// 대신 generateModelId()를 사용하세요.
// ID 정규화 함수 (레거시 호환용으로만 유지)
export const normalizeId = (str: string): string => 
  str.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_가-힣]/g, '').toLowerCase();

// 🔑 디바이스 생성 헬퍼 함수
// [SSOT] generateModelId()를 사용하여 canonical ID 생성
// - storage, connectivity, 순번은 ID에 절대 포함하지 않음
// - 형식: {brand}-{deviceType}-{series}-{modelEn}
export const createDevice = (params: Omit<UnifiedDevice, 'id' | 'searchTerms'>): UnifiedDevice => {
  // 🔥 SSOT: generateModelId()와 동일한 규칙으로 ID 생성 (storage 미포함)
  const id = generateModelId({
    brand: params.brand,
    deviceType: params.category, // '스마트폰' | '태블릿' | '웨어러블' → 내부에서 영어로 변환
    series: params.series,
    modelEn: params.modelEn
  });
  
  const searchTerms = [
    params.brand.toLowerCase(),
    params.category,
    params.series.toLowerCase(),
    params.modelEn.toLowerCase(),
    params.modelKo.toLowerCase(),
    params.storage.toLowerCase(),
    ...(params.modelNumber ? [params.modelNumber.toLowerCase()] : []),
    ...(params.color ? [params.color.toLowerCase()] : [])
  ];
  
  return {
    ...params,
    id,
    searchTerms: Array.from(new Set(searchTerms.filter(Boolean)))
  };
};

// 📱 iPhone 시리즈 생성 헬퍼
export interface IPhoneModelData {
  en: string;
  ko: string;
  storages: string[];
  year: number;
  recommended?: boolean;
  image?: string;
}

export interface IPhoneSeriesData {
  series: string;
  models: IPhoneModelData[];
}

// 📱 iPhone 이미지 경로 매핑 함수 (시리즈 대표 이미지 우선)
const getIPhoneImagePath = (series: string, modelEn: string): string => {
  const seriesFolder = series.replace(' ', ''); // "iPhone 16" -> "iPhone16"
  
  // 🎯 시리즈별 대표 이미지 (기본 모델 우선)
  const SERIES_REPRESENTATIVE_IMAGES = {
    'iPhone 17': '/assets/iPhone/iPhone16/iphone16.png', // TODO: iPhone 17 전용 이미지 준비 후 교체
    'iPhone 16': '/assets/iPhone/iPhone16/iphone16.png',
    'iPhone 15': '/assets/iPhone/iPhone15/iphone15.png', 
    'iPhone 14': '/assets/iPhone/iPhone14/iphone14.png',
    'iPhone 13': '/assets/iPhone/iPhone13/iphone13.png',
    'iPhone 12': '/assets/iPhone/iPhone12/iphone12.png',
    'iPhone 11': '/assets/iPhone/iPhone11/iphone11.jpg',
    'iPhone X': '/assets/iPhone/iPhoneX/iphone-x.jpg',
    'iPhone 8': '/assets/iPhone/iPhone8/iphone8.jpg',
    'iPhone 7': '/assets/iPhone/iPhone7/iphone7.jpg',
    'iPhone 6': '/assets/iPhone/iPhone6/iphone6.jpg',
    'iPhone SE': '/assets/iPhone/iPhoneSE/iphone-se.jpg'
  };
  
  // 시리즈별 특별 처리
  if (series === 'iPhone SE') {
    let sePath = '';
    if (modelEn.includes('2nd')) sePath = '/assets/iPhone/iPhoneSE/iphone-se-2nd.jpg';
    else if (modelEn.includes('3rd')) sePath = '/assets/iPhone/iPhoneSE/iphone-se-3rd.png';
    else sePath = '/assets/iPhone/iPhoneSE/iphone-se.jpg';
    
    // iPhone SE 특별 처리 완료
    return sePath;
  }
  
  if (series === 'iPhone X') {
    let xPath = '';
    if (modelEn === 'iPhone X') xPath = '/assets/iPhone/iPhoneX/iphone-x.jpg';
    else if (modelEn === 'iPhone XS') xPath = '/assets/iPhone/iPhoneX/iphone-xs.jpg';
    else if (modelEn === 'iPhone XS Max') xPath = '/assets/iPhone/iPhoneX/iphone-xs-max.jpg';
    else if (modelEn === 'iPhone XR') xPath = '/assets/iPhone/iPhoneX/iphone-xr.jpg';
    
    if (xPath) {
      // iPhone X 특별 처리 완료
      return xPath;
    }
  }
  
  // 확장자 결정 (시리즈별로 다름)
  let extension = 'png';
  if (['iPhone6', 'iPhone7', 'iPhone8', 'iPhone11'].includes(seriesFolder)) {
    extension = 'jpg';
  } else if (series === 'iPhone 12' && (modelEn.includes('Pro'))) {
    extension = 'jpg'; // iPhone12 Pro 시리즈만 jpg
  }
  
  // 시리즈별 파일명 매핑 (실제 파일명과 정확히 일치)
  let fileName = '';
  
  // TODO: iPhone 17 전용 이미지 준비 후 경로 교체 (현재 iPhone 16 이미지 임시 사용)
  if (series === 'iPhone 17') {
    if (modelEn === 'iPhone 17') fileName = 'iphone16';
    else if (modelEn === 'iPhone 17 AIR') fileName = 'iphone16';
    else if (modelEn === 'iPhone 17 Pro') fileName = 'iphone16pro';
    else if (modelEn === 'iPhone 17 Pro Max') fileName = 'iphone16pro-max';
    extension = 'png';
    return `/assets/iPhone/iPhone16/${fileName}.${extension}`;
  }
  if (series === 'iPhone 16') {
    if (modelEn === 'iPhone 16') fileName = 'iphone16';
    else if (modelEn === 'iPhone 16 Plus') fileName = 'iphone16plus';
    else if (modelEn === 'iPhone 16 Pro') fileName = 'iphone16pro';
    else if (modelEn === 'iPhone 16 Pro Max') fileName = 'iphone16pro-max';
    else if (modelEn === 'iPhone 16e') fileName = 'iphone16e';
  }
  else if (series === 'iPhone 15') {
    if (modelEn === 'iPhone 15') fileName = 'iphone15';
    else if (modelEn === 'iPhone 15 Plus') fileName = 'iphone15-plus';
    else if (modelEn === 'iPhone 15 Pro') fileName = 'iphone15-pro';
    else if (modelEn === 'iPhone 15 Pro Max') fileName = 'iphone15pro-max';
  }
  else if (series === 'iPhone 14') {
    if (modelEn === 'iPhone 14') fileName = 'iphone14';
    else if (modelEn === 'iPhone 14 Plus') fileName = 'iphone14plus';
    else if (modelEn === 'iPhone 14 Pro') fileName = 'iphone14pro';
    else if (modelEn === 'iPhone 14 Pro Max') fileName = 'iphone14pro-max';
  }
  else if (series === 'iPhone 13') {
    if (modelEn === 'iPhone 13') fileName = 'iphone13';
    else if (modelEn === 'iPhone 13 Mini') fileName = 'iphone13mini';
    else if (modelEn === 'iPhone 13 Pro') fileName = 'iphone13pro';
    else if (modelEn === 'iPhone 13 Pro Max') fileName = 'iphone13pro-max';
  }
  else if (series === 'iPhone 12') {
    if (modelEn === 'iPhone 12') fileName = 'iphone12';
    else if (modelEn === 'iPhone 12 Mini') fileName = 'iphone12mini';
    else if (modelEn === 'iPhone 12 Pro') fileName = 'iphone12pro';
    else if (modelEn === 'iPhone 12 Pro Max') fileName = 'iphone12pro-max';
  }
  else if (series === 'iPhone 11') {
    if (modelEn === 'iPhone 11') fileName = 'iphone11';
    else if (modelEn === 'iPhone 11 Pro') fileName = 'iphone11pro';
    else if (modelEn === 'iPhone 11 Pro Max') fileName = 'iphone11pro-max';
  }
  else if (series === 'iPhone 8') {
    if (modelEn === 'iPhone 8') fileName = 'iphone8';
    else if (modelEn === 'iPhone 8 Plus') fileName = 'iphone8plus';
  }
  else if (series === 'iPhone 7') {
    if (modelEn === 'iPhone 7') fileName = 'iphone7';
    else if (modelEn === 'iPhone 7 Plus') fileName = 'iphone7plus';
  }
  else if (series === 'iPhone 6') {
    if (modelEn === 'iPhone 6') fileName = 'iphone6';
    else if (modelEn === 'iPhone 6 Plus') fileName = 'iphone6plus';
    else if (modelEn === 'iPhone 6S') fileName = 'iphone6s';
    else if (modelEn === 'iPhone 6S Plus') fileName = 'iphone6splus';
  }
  
  // 🔄 매핑된 파일명이 없으면 시리즈 대표 이미지 사용
  if (!fileName && SERIES_REPRESENTATIVE_IMAGES[series]) {
    // 시리즈 대표 이미지 사용
    return SERIES_REPRESENTATIVE_IMAGES[series];
  }
  
  // 📁 최종 경로 생성
  const imagePath = `/assets/iPhone/${seriesFolder}/${fileName}.${extension}`;
  // iPhone 이미지 경로 생성 완료
  return imagePath;
};

export const createIPhoneSeries = (seriesData: IPhoneSeriesData): UnifiedDevice[] => {
  return seriesData.models.flatMap(model =>
    model.storages.map(storage => createDevice({
      brand: 'Apple',
      category: '스마트폰',
      series: seriesData.series,
      modelEn: model.en,
      modelKo: model.ko,
      storage,
      releaseYear: model.year,
      recommended: model.recommended,
      image: model.image || getIPhoneImagePath(seriesData.series, model.en)
    }))
  );
};

// 🤖 Samsung 시리즈 생성 헬퍼
export interface SamsungModelData {
  en: string;
  ko: string;
  storages: string[];
  year: number;
  recommended?: boolean;
  image?: string;
}

export interface SamsungSeriesData {
  series: string;
  models: SamsungModelData[];
}

// 🤖 Samsung 이미지 경로 매핑 함수 (실제 파일명과 일치)
const getSamsungImagePath = (series: string, modelEn: string): string => {
  // 🎯 시리즈별 대표 이미지 (기본 모델 우선)
  const SERIES_REPRESENTATIVE_IMAGES = {
    'Galaxy S': '/assets/samsung/galaxyS/GALAXY S24.png',
    'Galaxy Note': '/assets/samsung/galaxyNote/N20.jpg',
    'Galaxy Flip': '/assets/samsung/galaxyFlip/ZFLIP6.png',
    'Galaxy Fold': '/assets/samsung/galaxyFold/ZFOLD6.jpg'
  };
  
  // Galaxy S 시리즈 매핑
  if (series === 'Galaxy S') {
    // 특별한 파일명 매핑
    const galaxySMapping: Record<string, string> = {
      'Galaxy S25': '/assets/samsung/galaxyS/GALAXY S25.jpg',
      'Galaxy S25+': '/assets/samsung/galaxyS/GALAXY S25+.png', 
      'Galaxy S25 Ultra': '/assets/samsung/galaxyS/GALAXY S25U.jfif',
      'Galaxy S24': '/assets/samsung/galaxyS/GALAXY S24.png',
      'Galaxy S24+': '/assets/samsung/galaxyS/GALAXY S24+.png',
      'Galaxy S24 Ultra': '/assets/samsung/galaxyS/GALAXY S24U.png',
      'Galaxy S24 FE': '/assets/samsung/galaxyS/GALAXY S24FE.png',
      'Galaxy S23': '/assets/samsung/galaxyS/GALAXY S23.png',
      'Galaxy S23+': '/assets/samsung/galaxyS/GALAXY S23+.jpg',
      'Galaxy S23 Ultra': '/assets/samsung/galaxyS/GALAXY S23U.jpg',
      'Galaxy S23 FE': '/assets/samsung/galaxyS/GALAXY S23FE.jpg',
      'Galaxy S22': '/assets/samsung/galaxyS/GALAXY S22.png',
      'Galaxy S22+': '/assets/samsung/galaxyS/GALAXY S22+.png',
      'Galaxy S22 Ultra': '/assets/samsung/galaxyS/GALAXY S22U.png',
      'Galaxy S21': '/assets/samsung/galaxyS/GALAXY S21.png',
      'Galaxy S21+': '/assets/samsung/galaxyS/GALAXY S21+.jpg',
      'Galaxy S21 Ultra': '/assets/samsung/galaxyS/GALAXY S21U.jpg',
      'Galaxy S20': '/assets/samsung/galaxyS/GALAXY S20.png',
      'Galaxy S20+': '/assets/samsung/galaxyS/GALAXY S20+.jpg',
      'Galaxy S20 Ultra': '/assets/samsung/galaxyS/GALAXY S20U.jpg',
      'Galaxy S20 FE': '/assets/samsung/galaxyS/GALAXY S20FE.jpg',
      'Galaxy S10': '/assets/samsung/galaxyS/GALAXY S10.png',
      'Galaxy S10+': '/assets/samsung/galaxyS/GALAXY S10+.jpg',
      'Galaxy S10e': '/assets/samsung/galaxyS/GALAXY S10e.jpg',
      'Galaxy S10 5G': '/assets/samsung/galaxyS/GALAXY S10 5G.png',
      'Galaxy S9': '/assets/samsung/galaxyS/GALAXY S9.png',
      'Galaxy S9+': '/assets/samsung/galaxyS/GALAXY S9+.jpg',
      'Galaxy S8': '/assets/samsung/galaxyS/GALAXY S8.png',
      'Galaxy S8+': '/assets/samsung/galaxyS/GALAXY S8+.png'
    };
    
    if (galaxySMapping[modelEn]) {
      return galaxySMapping[modelEn];
    }
  }
  
  // Galaxy Note 시리즈 매핑
  if (series === 'Galaxy Note') {
    const noteMapping: Record<string, string> = {
      'Galaxy Note 20': '/assets/samsung/galaxyNote/N20.jpg',
      'Galaxy Note 20 Ultra': '/assets/samsung/galaxyNote/N20U.png',
      'Galaxy Note 10': '/assets/samsung/galaxyNote/N10.jpg',
      'Galaxy Note 10+': '/assets/samsung/galaxyNote/N10+.jpg',
      'Galaxy Note 9': '/assets/samsung/galaxyNote/N9.png',
      'Galaxy Note 8': '/assets/samsung/galaxyNote/N8.png'
    };
    
    if (noteMapping[modelEn]) {
      return noteMapping[modelEn];
    }
  }
  
  // Galaxy Flip 시리즈 매핑
  if (series === 'Galaxy Flip') {
    const flipMapping: Record<string, string> = {
      'Galaxy Z Flip6': '/assets/samsung/galaxyFlip/ZFLIP6.png',
      'Galaxy Z Flip5': '/assets/samsung/galaxyFlip/ZFLIP5.jpg',
      'Galaxy Z Flip4': '/assets/samsung/galaxyFlip/ZFLIP4.jpg',
      'Galaxy Z Flip3': '/assets/samsung/galaxyFlip/ZFLIP3.jpg'
    };
    
    if (flipMapping[modelEn]) {
      return flipMapping[modelEn];
    }
  }
  
  // Galaxy Fold 시리즈 매핑
  if (series === 'Galaxy Fold') {
    const foldMapping: Record<string, string> = {
      'Galaxy Z Fold6': '/assets/samsung/galaxyFold/ZFOLD6.jpg',
      'Galaxy Z Fold5': '/assets/samsung/galaxyFold/ZFOLD5.png',
      'Galaxy Z Fold4': '/assets/samsung/galaxyFold/ZFOLD4.jpg',
      'Galaxy Z Fold3': '/assets/samsung/galaxyFold/ZFOLD3.jpg'
    };
    
    if (foldMapping[modelEn]) {
      return foldMapping[modelEn];
    }
  }
  
  // 🔄 매핑이 없으면 시리즈 대표 이미지 사용
  return SERIES_REPRESENTATIVE_IMAGES[series] || '/assets/samsung/galaxyS/GALAXY S24.png';
};

export const createSamsungSeries = (seriesData: SamsungSeriesData): UnifiedDevice[] => {
  return seriesData.models.flatMap(model =>
    model.storages.map(storage => createDevice({
      brand: 'Samsung',
      category: '스마트폰',
      series: seriesData.series,
      modelEn: model.en,
      modelKo: model.ko,
      storage,
      releaseYear: model.year,
      recommended: model.recommended,
      image: model.image || getSamsungImagePath(seriesData.series, model.en)
    }))
  );
};

// 📟 iPad 시리즈 생성 헬퍼
export interface IPadModelData {
  en: string;
  ko: string;
  storages: string[];
  year: number;
  recommended?: boolean;
  image: string;
  modelNumbers?: string[];
  connectivityOptions?: string[];
}

export interface IPadSeriesData {
  series: string;
  models: IPadModelData[];
}

// 📟 iPad 이미지 경로 매핑 함수 (실제 파일명과 일치)
const getIPadImagePath = (series: string, modelEn: string): string => {
  // 🎯 시리즈별 대표 이미지 (기본 모델 우선)
  const SERIES_REPRESENTATIVE_IMAGES = {
    'iPad': '/assets/ipad/패드10세대.png',
    'iPad Air': '/assets/ipadAir/에어6세대.png',
    'iPad Mini': '/assets/ipadMini/미니7세대.png',
    'iPad Pro': '/assets/ipadPro/프로5_7세대 11_12.9.png'
  };
  
  // iPad 시리즈 매핑
  if (series === 'iPad') {
    const ipadMapping: Record<string, string> = {
      'iPad 10세대': '/assets/ipad/패드10세대.png',
      'iPad 9세대': '/assets/ipad/패드9세대.png',
      'iPad 8세대': '/assets/ipad/패드8세대.png',
      'iPad 7세대': '/assets/ipad/패드7세대.png',
      'iPad 6세대': '/assets/ipad/패드6세대.png', 
      'iPad 5세대': '/assets/ipad/패드5세대.png'
    };
    
    if (ipadMapping[modelEn]) {
      return ipadMapping[modelEn];
    }
  }
  
  // iPad Air 시리즈 매핑
  if (series === 'iPad Air') {
    const airMapping: Record<string, string> = {
      'iPad Air 6세대': '/assets/ipadAir/에어6세대.png',
      'iPad Air 5세대': '/assets/ipadAir/에어5세대.png',
      'iPad Air 4세대': '/assets/ipadAir/에어4세대.png',
      'iPad Air 3세대': '/assets/ipadAir/에어3세대.png'
    };
    
    if (airMapping[modelEn]) {
      return airMapping[modelEn];
    }
  }
  
  // iPad Mini 시리즈 매핑
  if (series === 'iPad Mini') {
    const miniMapping: Record<string, string> = {
      'iPad Mini 7세대': '/assets/ipadMini/미니7세대.png',
      'iPad Mini 6세대': '/assets/ipadMini/미니6세대.png',
      'iPad Mini 5세대': '/assets/ipadMini/미니5세대.png'
    };
    
    if (miniMapping[modelEn]) {
      return miniMapping[modelEn];
    }
  }
  
  // iPad Pro 시리즈 매핑
  if (series === 'iPad Pro') {
    const proMapping: Record<string, string> = {
      'iPad Pro 7세대 11인치': '/assets/ipadPro/프로5_7세대 11_12.9.png',
      'iPad Pro 7세대 12.9인치': '/assets/ipadPro/프로5_7세대 11_12.9.png',
      'iPad Pro 6세대 12.9인치': '/assets/ipadPro/프로6세대 12.9.png',
      'iPad Pro 5세대 11인치': '/assets/ipadPro/프로5세대 11.png',
      'iPad Pro 4세대 11인치': '/assets/ipadPro/프로4세대 11.png',
      'iPad Pro 4세대 12.9인치': '/assets/ipadPro/프로4세대 12.9.png',
      'iPad Pro 3세대 11인치': '/assets/ipadPro/프로3세대 11 M1.png',
      'iPad Pro 3세대 12.9인치': '/assets/ipadPro/프로3세대 12.9.png',
      'iPad Pro 2세대 10.5인치': '/assets/ipadPro/프로2세대 10.5.png',
      'iPad Pro 2세대 11인치': '/assets/ipadPro/프로2세대 11.png',
      'iPad Pro 1세대 9.7인치': '/assets/ipadPro/프로1세대 9.7.png',
      'iPad Pro 1세대 11인치': '/assets/ipadPro/프로1세대 11인치.png'
    };
    
    if (proMapping[modelEn]) {
      return proMapping[modelEn];
    }
  }
  
  // 🔄 매핑이 없으면 시리즈 대표 이미지 사용
  return SERIES_REPRESENTATIVE_IMAGES[series] || '/assets/ipad/iPadDefault.png';
};

export const createIPadSeries = (seriesData: IPadSeriesData): UnifiedDevice[] => {
  return seriesData.models.flatMap(model =>
    model.storages.flatMap(storage => {
      const connectivities = model.connectivityOptions || ['Wi-Fi'];
      return connectivities.map(connectivity => createDevice({
        brand: 'Apple',
        category: '태블릿',
        series: seriesData.series,
        modelEn: model.en,
        modelKo: model.ko,
        storage,
        connectivity,
        releaseYear: model.year,
        recommended: model.recommended,
        image: model.image || getIPadImagePath(seriesData.series, model.en),
        modelNumber: model.modelNumbers?.[0]
      }));
    })
  );
};

// 📟 Samsung 태블릿 시리즈 생성 헬퍼  
export interface SamsungTabletModelData {
  en: string;
  ko: string;
  storages: string[];
  year: number;
  recommended?: boolean;
  image?: string;
}

export interface SamsungTabletSeriesData {
  series: string;
  models: SamsungTabletModelData[];
}

// 📟 Samsung 태블릿 이미지 경로 매핑 함수 (실제 파일명과 일치)
const getSamsungTabletImagePath = (series: string, modelEn: string): string => {
  // 🎯 시리즈별 대표 이미지 (기본 모델 우선)
  const SERIES_REPRESENTATIVE_IMAGES = {
    'Galaxy Tab S': '/assets/samsung/galaxyTab/S_Series/S9_X710,X716.jpg',
    'Galaxy Tab A': '/assets/samsung/galaxyTab/A_Series/A9+_X210N.jpg'
  };
  
  // Galaxy Tab S 시리즈 매핑
  if (series === 'Galaxy Tab S') {
    const tabSMapping: Record<string, string> = {
      'Galaxy Tab S9': '/assets/samsung/galaxyTab/S_Series/S9_X710,X716.jpg',
      'Galaxy Tab S9+': '/assets/samsung/galaxyTab/S_Series/S9+_X810,X816.jpg',
      'Galaxy Tab S9 Ultra': '/assets/samsung/galaxyTab/S_Series/S9U_X910,X916.jpg',
      'Galaxy Tab S9 FE': '/assets/samsung/galaxyTab/S_Series/S9FE.jpg',
      'Galaxy Tab S9 FE+': '/assets/samsung/galaxyTab/S_Series/S9FE+.jpg',
      'Galaxy Tab S8': '/assets/samsung/galaxyTab/S_Series/S8_X700,X706.jpg',
      'Galaxy Tab S8+': '/assets/samsung/galaxyTab/S_Series/S8+_X800,X806.jpg',
      'Galaxy Tab S8 Ultra': '/assets/samsung/galaxyTab/S_Series/S8U_X900,X906.jpg',
      'Galaxy Tab S7': '/assets/samsung/galaxyTab/S_Series/S7_T870,T875.jpg',
      'Galaxy Tab S7+': '/assets/samsung/galaxyTab/S_Series/S7+_T970,T975,T976.jpg',
      'Galaxy Tab S7 FE': '/assets/samsung/galaxyTab/S_Series/S7FE_T733,T735,T736.jpg',
      'Galaxy Tab S6': '/assets/samsung/galaxyTab/S_Series/S6_T860,T865.jpg',
      'Galaxy Tab S6 Lite': '/assets/samsung/galaxyTab/S_Series/S6 LITE_P610,P615.jpg',
      'Galaxy Tab S10+': '/assets/samsung/galaxyTab/S_Series/S10+_X810,X825.jpg',
      'Galaxy Tab S10 FE': '/assets/samsung/galaxyTab/S_Series/S10FE_X520,X526.jpg',
      'Galaxy Tab S10 FE+': '/assets/samsung/galaxyTab/S_Series/S10FE+_X620,X626.jpg',
      'Galaxy Tab S10 Ultra': '/assets/samsung/galaxyTab/S_Series/S10U_X920.jpg'
    };
    
    if (tabSMapping[modelEn]) {
      return tabSMapping[modelEn];
    }
  }
  
  // Galaxy Tab A 시리즈 매핑
  if (series === 'Galaxy Tab A') {
    const tabAMapping: Record<string, string> = {
      'Galaxy Tab A9+': '/assets/samsung/galaxyTab/A_Series/A9+_X210N.jpg',
      'Galaxy Tab A9': '/assets/samsung/galaxyTab/A_Series/A9_X110.jpg',
      'Galaxy Tab A8 10.5': '/assets/samsung/galaxyTab/A_Series/A8 10.5_X200,X205.jpg',
      'Galaxy Tab A7 10.4': '/assets/samsung/galaxyTab/A_Series/A7 10.4_T500,T505.jpg',
      'Galaxy Tab A7 Lite': '/assets/samsung/galaxyTab/A_Series/A7 LITE_T220,T225.jpg',
      'Galaxy Tab A 10.5': '/assets/samsung/galaxyTab/A_Series/A 10.5_T590,T595.jpg',
      'Galaxy Tab A 10.1': '/assets/samsung/galaxyTab/A_Series/A 10.1_T510,T515.jpg',
      'Galaxy Tab A 8.0 (2019)': '/assets/samsung/galaxyTab/A_Series/A 8.0_T290,T295.jpg',
      'Galaxy Tab A 8.0 (2015)': '/assets/samsung/galaxyTab/A_Series/A 8.0_P200.jpg'
    };
    
    if (tabAMapping[modelEn]) {
      return tabAMapping[modelEn];
    }
  }
  
  // 🔄 매핑이 없으면 시리즈 대표 이미지 사용
  return SERIES_REPRESENTATIVE_IMAGES[series] || '/assets/samsung/galaxyTab/S_Series/S9_X710,X716.jpg';
};

export const createSamsungTabletSeries = (seriesData: SamsungTabletSeriesData): UnifiedDevice[] => {
  return seriesData.models.flatMap(model =>
    model.storages.map(storage => createDevice({
      brand: 'Samsung',
      category: '태블릿',
      series: seriesData.series,
      modelEn: model.en,
      modelKo: model.ko,
      storage,
      releaseYear: model.year,
      recommended: model.recommended,
      image: model.image || getSamsungTabletImagePath(seriesData.series, model.en)
    }))
  );
};

// 🎧 AirPods 생성 헬퍼
export interface AirPodsModelData {
  en: string;
  ko: string;
  connectivity: string;
  year: number;
  recommended?: boolean;
  image: string;
  modelNumber?: string;
}

export const createAirPods = (models: AirPodsModelData[]): UnifiedDevice[] => {
  return models.map(model => createDevice({
    brand: 'Apple',
    category: '웨어러블',
    series: model.en.includes('Pro') ? 'AirPods Pro' : 
           model.en.includes('Max') ? 'AirPods Max' : 'AirPods',
    modelEn: model.en,
    modelKo: model.ko,
    storage: model.connectivity,
    connectivity: model.connectivity,
    releaseYear: model.year,
    recommended: model.recommended,
    image: model.image,
    modelNumber: model.modelNumber
  }));
};
